/**
 * `event.error` is null for errors thrown by cross-origin scripts, and `event.reason` can be any
 * value. `trackError` assigns to `error.message`, which throws on both.
 */
function toError(value: unknown, fallbackMessage: string): Error {
  // Both `instanceof` and property reads can throw, because a reason can be a proxy with hostile
  // traps. Anything unreadable falls back rather than escaping the listener and losing the event.
  try {
    if (value instanceof Error) {
      return value;
    }

    if (typeof value === 'string') {
      return new Error(value || fallbackMessage);
    }

    // The legacy HTTP client rejects with `{ ...body, status }`, so a server message arrives on a
    // plain object. An Error from another realm fails `instanceof` here, so carry over its identity
    // and stack instead of reporting the one synthesized on the next line.
    const error = new Error(stringField(value, 'message') || fallbackMessage);
    const name = stringField(value, 'name');
    const stack = stringField(value, 'stack');
    if (name) {
      error.name = name;
    }
    if (stack) {
      error.stack = stack;
    }

    return error;
  } catch {
    return new Error(fallbackMessage);
  }
}

// Guarded per field, not per call: a proxy can return `message` and throw for `name`, and a
// readable message is worth keeping.
function stringField(value: unknown, key: string): string {
  try {
    const field = (value as Record<string, unknown> | null | undefined)?.[key];
    return typeof field === 'string' ? field : '';
  } catch {
    return '';
  }
}

export function captureExceptions() {
  const exceptions: Error[] = [];

  const handleError = (event: ErrorEvent) => {
    exceptions.push(toError(event.error, event.message || 'Unknown error'));
  };

  const handleRejection = (event: PromiseRejectionEvent) => {
    exceptions.push(toError(event.reason, 'Unhandled promise rejection'));
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleRejection);

    return exceptions;
  };
}
